import { Card } from '../../../layout/Card.jsx';
import { Avatar } from '../../../layout/Avatar.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useDuration } from '../../../shared/useDuration.js';

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/') || url.startsWith('/uploads/')) return url;
  return `/api/children/photos/${url.replace(/^\//, '')}`;
}

function MemberRow({ member, checked, focused, onToggle, onFocus }) {
  const session = member.session || {};
  const child = member.child || session.childId || {};
  const totals = member.totals || {};
  const duration = useDuration(session.startTime, 30000);

  const due = Number(totals.due || 0);
  const companions = Number(totals.companionsCount || 0);

  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 transition-colors cursor-pointer ${
        focused ? 'bg-orange-soft' : 'hover:bg-paper-2'
      }`}
      onClick={() => onFocus?.(session._id)}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => { e.stopPropagation(); onToggle?.(session._id); }}
        onClick={(e) => e.stopPropagation()}
        style={{ width: 17, height: 17, flex: 'none' }}
        aria-label={`اختيار ${child.name || 'الطفل'}`}
      />
      <Avatar src={resolvePhoto(child.photoUrl)} name={child.name || '?'} id={child._id} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[13px] truncate">{child.name || '—'}</div>
        <div className="text-[11px] text-ink-3 flex items-center gap-1.5">
          <Icon name="clock" size={11} />
          <span className="font-mono">{duration.formatted}</span>
          {companions > 0 ? <Pill variant="muted" className="!px-1.5 !py-0 !text-[10px]">+{companions} مرافق</Pill> : null}
        </div>
      </div>
      <div className="text-left flex-none">
        <div className="font-extrabold text-[13px]" style={{ color: due > 0 ? '#B91C1C' : '#15803D' }}>
          {formatIQD(due)}
        </div>
        {Number(totals.cafeteria || 0) + Number(totals.toys || 0) > 0 ? (
          <div className="text-[10px] text-ink-3">
            منها {formatIQD(Number(totals.cafeteria || 0) + Number(totals.toys || 0))} نقطة بيع
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * قائمة كل الأطفال النشطين على الكارت الحالي، مع اختيار من يُحاسَب.
 * هذه هي الشاشة التي تحل مشكلة «يظهر طفل واحد ثم الثاني بعد التسديد».
 */
export function GroupMembersList({
  group,
  selectedIds,
  focusedId,
  onToggle,
  onSelectAll,
  onClearAll,
  onFocus
}) {
  const members = Array.isArray(group?.members) ? group.members : [];
  const allSelected = members.length > 0 && selectedIds.length === members.length;
  const companionsCount = Number(group?.companionsCount || 0);

  return (
    <Card
      header={`الأطفال على الكارت (${members.length})`}
      sub={group?.cardUid ? `كارت ${group.cardUid}` : 'بدون كارت'}
    >
      {members.length > 1 ? (
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            className={`btn btn-sm flex-1 justify-center ${allSelected ? 'btn-primary' : ''}`}
            onClick={onSelectAll}
          >
            <Icon name="check" size={13} /> تحديد الكل ({members.length})
          </button>
          <button
            type="button"
            className="btn btn-sm flex-1 justify-center"
            onClick={onClearAll}
            disabled={!selectedIds.length}
          >
            إلغاء التحديد
          </button>
        </div>
      ) : null}

      <div className="border border-line rounded-[10px] divide-y divide-line overflow-hidden">
        {members.map((m) => (
          <MemberRow
            key={m.session._id}
            member={m}
            checked={selectedIds.includes(m.session._id)}
            focused={focusedId === m.session._id}
            onToggle={onToggle}
            onFocus={onFocus}
          />
        ))}
      </div>

      {companionsCount > 0 ? (
        <div className="mt-2 text-[11.5px] text-ink-3 flex items-center gap-1.5">
          <Icon name="users" size={13} />
          {companionsCount} مرافق على هذا الكارت
          {Number(group?.companionsAmount || 0) > 0
            ? ` • ${formatIQD(group.companionsAmount)}`
            : ' • مجاناً'}
        </div>
      ) : null}

      {members.length > 1 ? (
        <div className="mt-2 text-[11px] text-ink-3 leading-relaxed">
          يمكنك محاسبة طفل واحد وإخراجه، ويبقى الباقون داخل القاعة على نفس الكارت.
          الكارت يعود للمخزن بعد خروج آخر طفل.
        </div>
      ) : null}
    </Card>
  );
}
